const TOKEN_KEY = 'nayma_admin_token'
const REFRESH_KEY = 'nayma_admin_refresh'
const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1'

export function getToken() {
  return sessionStorage.getItem(TOKEN_KEY)
}

export function getRefreshToken() {
  return sessionStorage.getItem(REFRESH_KEY)
}

export function setSession(access: string, refresh: string) {
  sessionStorage.setItem(TOKEN_KEY, access)
  sessionStorage.setItem(REFRESH_KEY, refresh)
}

export function clearSession() {
  sessionStorage.removeItem(TOKEN_KEY)
  sessionStorage.removeItem(REFRESH_KEY)
}

export class ApiError extends Error {
  status: number
  code?: string
  constructor(status: number, message: string, code?: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

function detailMessage(data: unknown, fallback: string): { message: string; code?: string } {
  if (typeof data === 'object' && data && 'detail' in data) {
    const detail = (data as { detail: unknown }).detail
    if (typeof detail === 'string') return { message: detail }
    if (typeof detail === 'object' && detail && 'message' in detail) {
      const d = detail as { message?: string; code?: string }
      return { message: d.message || fallback, code: d.code }
    }
    return { message: JSON.stringify(detail) }
  }
  return { message: fallback }
}

let refreshPromise: Promise<boolean> | null = null

async function tryRefresh(): Promise<boolean> {
  const refresh = getRefreshToken()
  if (!refresh) return false
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      })
      if (!res.ok) {
        clearSession()
        return false
      }
      const data = (await res.json()) as {
        access_token: string
        refresh_token: string
      }
      setSession(data.access_token, data.refresh_token)
      return true
    } catch {
      clearSession()
      return false
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

function redirectToLogin() {
  clearSession()
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.href = '/login'
  }
}

export async function api<T>(path: string, init: RequestInit = {}, allowRefresh = true): Promise<T> {
  const headers = new Headers(init.headers || {})
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json')
  const token = getToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers })

  if (res.status === 401 && allowRefresh && !path.startsWith('/auth/')) {
    const ok = await tryRefresh()
    if (ok) return api<T>(path, init, false)
    redirectToLogin()
    throw new ApiError(401, 'Сессия истекла — войдите снова')
  }

  if (res.status === 204) return undefined as T
  const text = await res.text()
  let data: unknown = null
  try {
    data = text ? JSON.parse(text) : null
  } catch {
    data = text
  }
  if (!res.ok) {
    const { message, code } = detailMessage(data, text || res.statusText)
    if (res.status === 401) redirectToLogin()
    throw new ApiError(res.status, message, code)
  }
  return data as T
}

export async function login(email: string, password: string, totpCode?: string) {
  const body: Record<string, string> = { email, password }
  if (totpCode) body.totp_code = totpCode
  const data = await api<{
    access_token: string
    refresh_token: string
    user: { role: string; name: string; email?: string }
  }>(
    '/auth/login',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    false,
  )
  if (data.user.role !== 'admin') {
    throw new ApiError(403, 'Доступ только для сотрудников NaymA')
  }
  setSession(data.access_token, data.refresh_token)
  return data.user
}
